import React, {ComponentType, FC, type JSX} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';

export function ColorBubble({colors, type, className}: ColorBubbleProps) {
  const testId = getColorBubbleTestId(type, colors);
  const Component = componentsByType[type];
  return <Component testId={testId} className={className} colors={colors} />;
}

type ColorDescriptor = {
  hexCode: string;
  isLightColor?: boolean;
};

type ColorBubbleType = 'gradient' | 'simple';

export type ColorBubbleDescriptor = {
  colors: ColorDescriptor[];
  type: ColorBubbleType;
  productId: number;
};

export type ColorBubbleProps = {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  className?: string;
} & ColorBubbleDescriptor;

type ColorBubbleComponentProps = {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  className?: string;
  colors: ColorDescriptor[];
  testId: string;
};

const Bubble = styled.div`
  box-sizing: border-box;
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 100%;
  border: none;
  background-repeat: no-repeat;
`;

const SimpleColorLightBubbleBorder = styled(Bubble)`
  border: 1px solid ${COLORS['Outline.Grey']};
`;

const SimpleColorBubble: FC<ColorBubbleComponentProps> = ({
  colors,
  testId,
  ...props
}) => {
  const styles =
    colors[0] && colors[1]
      ? {
          backgroundImage: `linear-gradient(90deg, ${colors[0].hexCode} 50%, ${colors[1].hexCode} 50%)`,
        }
      : {
          backgroundColor: colors[0]?.hexCode ?? 'transparent',
        };

  if (colors.some(color => color.isLightColor)) {
    return (
      <SimpleColorLightBubbleBorder
        data-testid={testId}
        style={styles}
        {...props}
      />
    );
  }

  return <Bubble data-testid={testId} style={styles} {...props} />;
};

const GradientColorBubble: FC<ColorBubbleComponentProps> = ({
  colors,
  testId,
  ...props
}) => {
  return (
    <SimpleColorLightBubbleBorder
      data-testid={testId}
      style={{
        backgroundImage: `linear-gradient(
          45deg,
          ${colors
            .map((color, i) => `${color.hexCode} ${i * (100 / colors.length)}%`)
            .join(', ')}
        )`,
      }}
      {...props}
    />
  );
};

const componentsByType: Record<
  ColorBubbleType,
  ComponentType<ColorBubbleComponentProps>
> = {
  gradient: GradientColorBubble,
  simple: SimpleColorBubble,
};

function getColorBubbleTestId(
  type: ColorBubbleType,
  colors: ColorDescriptor[],
) {
  return `ColorBubble-${type}-${colors.map(color => color.hexCode).join(',')}`;
}
